import MaskedView from "@react-native-masked-view/masked-view";
import { LinearGradient } from "expo-linear-gradient";
import { useCallback, useRef, useState, type PropsWithChildren } from "react";
import {
  Platform,
  StyleSheet,
  useWindowDimensions,
  View,
  type LayoutChangeEvent,
  type StyleProp,
  type ViewProps,
  type ViewStyle
} from "react-native";

import { BackdropScrim } from "@/components/HeaderBackdropScrim";

type WindowFrame = {
  height: number;
  x: number;
  y: number;
};

/**
 * Alpha-fades the edges of a scrolling content plane so it dissolves at the
 * boundary instead of clipping. The scroll owner may be this viewport or an
 * ancestor.
 *
 * iOS and web can safely mask the content plane. Android cannot: native-stack
 * pop snapshots a large MaskedView's offscreen texture as black. Keep
 * Android's scrolling rows on the ordinary hardware canvas and place only
 * two tiny, backdrop-aligned masked caps above them instead.
 */
export function ScrollEdgeFade({
  children,
  top = 0,
  bottom = 0,
  onLayout,
  style
}: PropsWithChildren<{
  top?: number;
  bottom?: number;
  onLayout?: ViewProps["onLayout"];
  style?: StyleProp<ViewStyle>;
}>) {
  const containerRef = useRef<View>(null);
  const viewport = useWindowDimensions();
  const [windowFrame, setWindowFrame] = useState<WindowFrame | null>(null);
  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      onLayout?.(event);
      if (Platform.OS !== "android") return;
      containerRef.current?.measureInWindow((x, y, _width, height) => {
        setWindowFrame((current) =>
          current &&
          Math.abs(current.x - x) < 0.5 &&
          Math.abs(current.y - y) < 0.5 &&
          Math.abs(current.height - height) < 0.5
            ? current
            : { height, x, y }
        );
      });
    },
    [onLayout]
  );

  if (top <= 0 && bottom <= 0) {
    return <View onLayout={onLayout} style={[styles.fade, style]}>{children}</View>;
  }

  if (Platform.OS === "web") {
    const stops = [
      top > 0 ? `transparent 0px, black ${top}px` : "black 0px",
      bottom > 0 ? `black calc(100% - ${bottom}px), transparent 100%` : "black 100%"
    ].join(", ");
    const maskImage = `linear-gradient(to bottom, ${stops})`;
    return (
      <View
        onLayout={onLayout}
        style={[
          styles.fade,
          style,
          styles.webClip,
          { maskImage, WebkitMaskImage: maskImage } as ViewStyle
        ]}
      >
        {children}
      </View>
    );
  }

  if (Platform.OS === "android") {
    const alignedTopSurface = windowFrame
      ? [
          styles.androidBackdropSurface,
          {
            height: viewport.height,
            left: -windowFrame.x,
            top: -windowFrame.y,
            width: viewport.width
          }
        ]
      : undefined;
    const alignedBottomSurface = windowFrame
      ? [
          styles.androidBackdropSurface,
          {
            height: viewport.height,
            left: -windowFrame.x,
            top: -(windowFrame.y + windowFrame.height - bottom),
            width: viewport.width
          }
        ]
      : undefined;

    return (
      <View
        ref={containerRef}
        onLayout={handleLayout}
        style={[styles.fade, styles.androidClip, style]}
      >
        {children}
        {top > 0 && alignedTopSurface ? (
          <View
            collapsable={false}
            pointerEvents="none"
            style={[styles.androidEdge, styles.androidEdgeTop, { height: top }]}
          >
            <BackdropScrim
              style={StyleSheet.absoluteFill}
              surfaceStyle={alignedTopSurface}
              variant="top-edge"
            />
          </View>
        ) : null}
        {bottom > 0 && alignedBottomSurface ? (
          <View
            collapsable={false}
            pointerEvents="none"
            style={[styles.androidEdge, styles.androidEdgeBottom, { height: bottom }]}
          >
            <BackdropScrim
              style={StyleSheet.absoluteFill}
              surfaceStyle={alignedBottomSurface}
              variant="bottom-edge"
            />
          </View>
        ) : null}
      </View>
    );
  }

  return (
    <MaskedView
      onLayout={onLayout}
      style={[styles.fade, style]}
      maskElement={
        <View style={styles.maskShape}>
          {top > 0 ? (
            <LinearGradient colors={["transparent", "black"]} style={{ height: top }} />
          ) : null}
          <View style={styles.maskOpaque} />
          {bottom > 0 ? (
            <LinearGradient colors={["black", "transparent"]} style={{ height: bottom }} />
          ) : null}
        </View>
      }
    >
      {children}
    </MaskedView>
  );
}

const styles = StyleSheet.create({
  fade: {
    flex: 1,
    minHeight: 0
  },
  androidClip: {
    overflow: "hidden"
  },
  // A mask only controls paint; Safari can still hit-test transformed children
  // after they have moved outside this box. Clip the actual web viewport too so
  // faded queue rows cannot intercept controls above or below it.
  webClip: {
    overflow: "hidden"
  },
  androidBackdropSurface: {
    position: "absolute"
  },
  androidEdge: {
    left: 0,
    overflow: "hidden",
    position: "absolute",
    right: 0,
    zIndex: 2
  },
  androidEdgeTop: {
    top: 0
  },
  androidEdgeBottom: {
    bottom: 0
  },
  maskOpaque: {
    backgroundColor: "black",
    flex: 1
  },
  maskShape: {
    flex: 1
  }
});
